// Effort / timer RPC access layer for the internal-ops `/hub/effort` page.
//
// Isolated module (NOT appended to the 222 KB api/auth.js) that reuses the same
// helper shape as auth.js's riRpc / two-layer write pattern. See
// `.clinerules` §"Error handling":
//   - READ RPCs return { data, error } from Supabase. `error` is Postgres-level
//     and must never reach the user — throw. Reads return the legacy
//     { error:false, data, message } envelope so page code reads `resp.data`.
//   - WRITE RPCs surface a user-facing failure in one of two ways: a
//     { success:false, error, message } payload inside `data` (thrown with that
//     message), or a Postgres P0001 `raise exception` the backend wrote. Anything
//     else is raw Postgres text. `effortWriteMessage` decides what a user may see.
//     Duplicate, inline edit and the Log and Verify dialogs use it; the timer stop in
//     RunningTimerHero still shows `e.message` and should move to it.
//
// ── CONTRACT (verified live) ─────────────────────────────────────────────────
// Field names below were confirmed against the live database (12_EFFORT_RESPONSE,
// 2026-09-08). fn_my_timesheet returns, per entry:
//   { id, at, minutes, what, client, client_id, task, task_id, service,
//     actor_type:'human'|'agent', actor, scope:'mine'|'agent_on_my_work',
//     cost, needs_review, corrected }
// per day: { day, minutes, entries, unusual }; top level adds `can_read_all`,
// `is_own`, `totals`
// { hours, entries, clients } and `agent_totals` { cost, entries }. An empty
// range returns days:[]/entries:[]/hours:0; a permission denial RAISES (→
// <NotPermitted>). Do NOT sum totals + agent_totals — hours and dollars are
// shown side by side, never on one axis.
//
// `running` is null or the 13-key object (pinned live 2026-09-08,
// 17_RUNNING_TIMER_RESPONSE):
//   { id, started_at, minutes, hours, over_8h, what, task, task_id,
//     client, client_id, thread_id, service, suggested }
// Same field names as an entry, so the hero binds with no special-casing.
// `suggested` is computed server-side as coalesce(start note, task title,
// thread subject) and matches fn_timer_start's — never re-derive it in the FE.
// `over_8h` (NOT needs_review) carries the >8h rule; the threshold lives in the
// database, so the FE must not recompute it from elapsed.

import { supabase } from '../../lib/supabase';

const wrapSupabase = (data) => ({ error: false, data, message: 'ok' });

// Postgres-level failure → a thrown Error carrying the legacy `response.data`
// envelope, mirroring auth.js's wrapSupabaseError so existing error call sites
// keep working. Not user-facing wording, except a P0001 (see effortWriteMessage).
const throwRpc = (err, label) => {
  const detail = err?.message || 'Supabase RPC failed';
  const message = label ? `${label}: ${detail}` : detail;
  const error = new Error(message);
  error.code = err?.code;
  error.response = { data: { error: true, message, data: null } };
  throw error;
};

/**
 * The text of a failed effort write that is safe to show a user.
 *
 * Only backend-authored wording is returned: a plain `Error` with no `.response` and
 * no `.code` (the `{ success:false, message }` path of `effortWrite`), or a Postgres
 * `P0001` (a `raise exception` the backend wrote), with the `<label>: ` that
 * `throwRpc` prepends stripped. Every other failure — a permission or constraint
 * error, a network failure, a programming error — returns `fallback`, so raw
 * Postgres text never reaches the row. The caller logs the raw error.
 *
 * @param {unknown} err The rejection from an effort write wrapper.
 * @param {string} label The label that wrapper passed to `effortWrite`, e.g. `'log effort'`.
 * @param {string} fallback Shown when the error carries no safe text.
 * @returns {string}
 */
export const effortWriteMessage = (err, label, fallback) => {
  const plain = err instanceof Error && Object.getPrototypeOf(err) === Error.prototype;
  const backendAuthored = err?.code === 'P0001' || (plain && !err.response && !err.code);
  if (!backendAuthored || typeof err?.message !== 'string') return fallback;
  const prefix = `${label}: `;
  const text = err.message.startsWith(prefix) ? err.message.slice(prefix.length) : err.message;
  return text.trim() || fallback;
};

/**
 * READ RPC: Postgres error → throw (never shown to the user); success returns
 * Supabase's `data` wrapped in the legacy envelope so callers read `resp.data`.
 * @param {string} name RPC function name.
 * @param {object} params RPC arguments.
 * @param {string} [label] Human-readable descriptor for error messages.
 */
async function effortRead(name, params, label) {
  const { data, error } = await supabase.rpc(name, params);
  if (error) throwRpc(error, label);
  return wrapSupabase(data);
}

/**
 * WRITE RPC: Postgres error → throw. If the payload carries an explicit
 * user-facing failure (the repo's two-layer `{ success:false, error, message }`
 * shape), throw that message verbatim. Otherwise return `data` as-is — the
 * effort write RPCs are documented to return a bare uuid or a plain result
 * object (e.g. fn_timer_stop → { id, minutes, description, needs_review }).
 */
async function effortWrite(name, params, label) {
  const { data, error } = await supabase.rpc(name, params);
  if (error) throwRpc(error, label);
  if (data && typeof data === 'object' && data.success === false) {
    throw new Error(
      data.message || (typeof data.error === 'string' && data.error) || `${label} failed`,
    );
  }
  return data;
}

/**
 * One person's effort over a window. `p_member_id` null = the caller's own
 * timesheet (always allowed); a non-null id requires `effort.read.all` and the
 * RPC raises otherwise (surfaced as <NotPermitted>, distinct from empty).
 * Returns the verified shape documented in the file header.
 */
export const getMyTimesheet = (from, to, memberId = null) =>
  effortRead(
    'fn_my_timesheet',
    { p_from: from, p_to: to, p_member_id: memberId },
    'timesheet',
  );

/**
 * The people whose timesheets the caller may view — the switcher's options.
 * Self first, then everyone alphabetically. Without `effort.read.all` the backend
 * returns only the caller, so the switcher renders unconditionally (one option)
 * and never branches on capability. Returns
 * [{ id, name, is_me, member_type }]. Agents are excluded by design — their
 * effort appears inside a human's timesheet, not as a person you switch to.
 */
export const getEffortMembers = () =>
  effortRead('fn_effort_members', {}, 'effort members');

/**
 * Start the running timer against a work item. Called from the app shell in
 * production (Charles's seam); exposed here so the interim TimerProvider and
 * tests can drive it. Returns { id, started_at, stopped_previous, suggested }.
 */
export const startTimer = ({
  taskId = null,
  threadId = null,
  clientId = null,
  meetingId = null,
  note = null,
} = {}) =>
  effortWrite(
    'fn_timer_start',
    {
      p_task_id: taskId,
      p_thread_id: threadId,
      p_client_id: clientId,
      p_meeting_id: meetingId,
      p_note: note,
    },
    'start timer',
  );

/** Stop the running timer. Returns { id, minutes, description, needs_review }. */
export const stopTimer = (description, reason = null) =>
  effortWrite(
    'fn_timer_stop',
    { p_description: description, p_reason: reason },
    'stop timer',
  );

/**
 * Log effort after the fact. `description` must be non-empty — the RPC raises on
 * an empty description, and the dialog enforces the same client-side. Returns a
 * uuid.
 */
export const logEffort = ({
  minutes,
  description,
  taskId = null,
  threadId = null,
  clientId = null,
  when = null,
  service = null,
}) =>
  effortWrite(
    'fn_effort_log',
    {
      p_minutes: minutes,
      p_description: description,
      p_task_id: taskId,
      p_thread_id: threadId,
      p_client_id: clientId,
      p_when: when,
      p_service: service,
    },
    'log effort',
  );

/**
 * Soft-delete an entry (§5f.3 of the backend request): it leaves the timesheet, the day
 * and week minutes and the cost views, and `restoreEffort` brings it back. Returns the
 * deleted id. Only `p_effort_id` is sent; `reason` goes along only when one is given,
 * because a parameter the database does not declare answers PGRST202.
 *
 * @param {{effortId: string, reason?: string|null}} args
 */
export const deleteEffort = ({ effortId, reason = null }) =>
  effortWrite(
    'fn_effort_delete',
    {
      p_effort_id: effortId,
      ...(reason != null ? { p_reason: reason } : {}),
    },
    'delete effort',
  );

/**
 * Undo a soft delete. Returns the restored id.
 *
 * @param {{effortId: string}} args
 */
export const restoreEffort = ({ effortId }) =>
  effortWrite(
    'fn_effort_restore',
    { p_effort_id: effortId },
    'restore effort',
  );

/**
 * Correct an existing entry. Server-side this writes a NEW row (the original is
 * kept and excluded from v_effort_cost); the UI only ever says "Edit" and never
 * exposes that a row was created. Returns the new row's uuid.
 *
 * `when` (the new start, an ISO instant), `clientId`, `taskId` and `service` (the
 * free-text project label) are the move parameters. Each is sent ONLY when it is given (not null or undefined), because the
 * deployed signature may not declare them yet and an undeclared parameter answers
 * PGRST202, failing an edit that never wanted to move anything. A null would not clear a
 * value either (the backend reads null as "keep"), so there is nothing to send for it.
 */
export const correctEffort = ({
  effortId, minutes, description, reason = null, when, clientId, taskId, service,
}) =>
  effortWrite(
    'fn_effort_correct',
    {
      p_effort_id: effortId,
      p_minutes: minutes,
      p_description: description,
      p_reason: reason,
      ...(when != null ? { p_when: when } : {}),
      ...(clientId != null ? { p_client_id: clientId } : {}),
      ...(taskId != null ? { p_task_id: taskId } : {}),
      ...(service != null ? { p_service: service } : {}),
    },
    'correct effort',
  );
